import { useContext, useState, type FormEvent } from 'react'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { AuthContext } from '@/context/auth-context'
import { OutlookLinkRefusalCode, OutlookLinkRefusedError } from '../fetch/link-code-refusal'
import postLinkCodeConsume from '../fetch/post-link-code-consume'
import postLinkCodePreview from '../fetch/post-link-code-preview'

type LinkStep = { kind: 'enter-code' } | { kind: 'confirm'; code: string; microsoftSignInName: string | null } | { kind: 'linked' }

const kRefusalMessages: Record<OutlookLinkRefusalCode, string> = {
  [OutlookLinkRefusalCode.CodeInvalid]: 'This code is not valid or has expired. Get a new code in Outlook and try again.',
  [OutlookLinkRefusalCode.AccountNotEligible]: 'Sign in with your Paxton account to link Outlook.',
  [OutlookLinkRefusalCode.IdentityAlreadyLinked]:
    'This Microsoft account is already linked to a different Paxton account. Contact Paxton support to change it.',
  [OutlookLinkRefusalCode.AccountAlreadyLinked]: 'Your Paxton account is already linked to a different Microsoft account. Contact Paxton support to change it.',
}

function describeFailure(cause: unknown, fallback: string): string {
  return cause instanceof OutlookLinkRefusedError ? kRefusalMessages[cause.code] : fallback
}

/**
 * Links the Microsoft account used in Outlook to the signed-in Paxton account. The user brings a
 * one-time code from the Outlook pane, sees which Microsoft account it belongs to, and confirms.
 *
 * The code is a bearer credential until it is used, so it arrives only through the input. This
 * page never reads it from the address: analytics loaded before React already read the URL. The
 * input sits inside the session-replay block classes for Amplitude and Sentry.
 */
export default function OutlookLinkPage() {
  const { user } = useContext(AuthContext)
  const [codeInput, setCodeInput] = useState('')
  const [step, setStep] = useState<LinkStep>({ kind: 'enter-code' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const code = codeInput.trim()
    if (!code || busy) return
    setBusy(true)
    setError(null)
    try {
      const preview = await postLinkCodePreview(code)
      setCodeInput('')
      setStep({ kind: 'confirm', code, microsoftSignInName: preview.microsoftSignInName })
    } catch (cause) {
      setError(describeFailure(cause, 'The code could not be checked. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirm(code: string) {
    setBusy(true)
    setError(null)
    try {
      await postLinkCodeConsume(code)
      setStep({ kind: 'linked' })
    } catch (cause) {
      setError(describeFailure(cause, 'The accounts could not be linked. Please try again.'))
      // A refused code cannot succeed on a second click; send the user back to enter a new one.
      if (cause instanceof OutlookLinkRefusedError) setStep({ kind: 'enter-code' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-col gap-4 px-6 py-10">
      <h1 className="text-xl font-semibold text-brand-neutral-900">Link Paxton to Outlook</h1>

      {step.kind === 'enter-code' && (
        <form className="flex flex-col gap-3" onSubmit={(event) => void handleSubmitCode(event)}>
          <p className="text-sm text-brand-neutral-700">Enter or paste the code shown in the Paxton pane in Outlook.</p>
          <div className="amp-block sentry-block">
            <label className="flex flex-col gap-1 text-sm font-medium text-brand-neutral-700" htmlFor="outlook-link-code">
              Link code
              <input
                id="outlook-link-code"
                className="w-full rounded border-0 font-mono uppercase tracking-widest ring-1 ring-inset ring-brand-neutral-400 focus:outline-hidden focus:ring-inset focus:ring-brand-400"
                value={codeInput}
                onChange={(event) => setCodeInput(event.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
              />
            </label>
          </div>
          {error && <InlineMessage variant="error">{error}</InlineMessage>}
          <Button type="submit" variant="filled" color="primary" size="md" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
        </form>
      )}

      {step.kind === 'confirm' && (
        <section className="flex flex-col gap-3" aria-label="Confirm link">
          <p className="text-sm text-brand-neutral-700">Link these two accounts?</p>
          <dl className="flex flex-col gap-2 rounded bg-brand-neutral-100 p-3 text-sm">
            <div>
              <dt className="font-medium text-brand-neutral-600">Microsoft account in Outlook</dt>
              <dd className="break-all text-brand-neutral-900">{step.microsoftSignInName ?? 'A Microsoft work or school account'}</dd>
            </div>
            <div>
              <dt className="font-medium text-brand-neutral-600">Paxton account</dt>
              <dd className="break-all text-brand-neutral-900">{user?.email ?? 'Your signed-in Paxton account'}</dd>
            </div>
          </dl>
          <InlineMessage variant="warning">
            Continue only if you requested this code yourself in Outlook just now. Anyone signed in to Outlook as this Microsoft account will be able to open
            your Paxton account from Outlook.
          </InlineMessage>
          {error && <InlineMessage variant="error">{error}</InlineMessage>}
          <div className="flex gap-2">
            <Button type="button" variant="filled" color="primary" size="md" disabled={busy} onClick={() => void handleConfirm(step.code)}>
              {busy ? 'Linking…' : 'Link accounts'}
            </Button>
            <Button type="button" variant="outlined" color="primary" size="md" disabled={busy} onClick={() => setStep({ kind: 'enter-code' })}>
              Cancel
            </Button>
          </div>
        </section>
      )}

      {step.kind === 'linked' && <InlineMessage variant="info">Your accounts are linked. Return to Outlook and select “I’ve linked my account”.</InlineMessage>}
    </main>
  )
}
